import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const MENU_GAP = 8;
const VIEWPORT_PADDING = 8;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function FloatingActionMenu({
  id,
  activeId,
  onActiveIdChange,
  className = "",
  triggerClassName = "",
  panelClassName = "",
  ariaLabel = "Open actions",
  renderTrigger,
  children,
}) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const frameRef = useRef(null);
  const [buttonRect, setButtonRect] = useState(null);
  const [position, setPosition] = useState(null);
  const isOpen = activeId === id;

  const closeMenu = useCallback(() => {
    onActiveIdChange?.(null);
    setButtonRect(null);
    setPosition(null);
  }, [onActiveIdChange]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !panelRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();
    const hasSpaceBelow =
      window.innerHeight - rect.bottom >= panelRect.height + MENU_GAP + VIEWPORT_PADDING;
    const top = hasSpaceBelow
      ? rect.bottom + MENU_GAP
      : Math.max(VIEWPORT_PADDING, rect.top - panelRect.height - MENU_GAP);
    const maxLeft = window.innerWidth - panelRect.width - VIEWPORT_PADDING;
    const left = clamp(
      rect.right - panelRect.width,
      VIEWPORT_PADDING,
      Math.max(VIEWPORT_PADDING, maxLeft),
    );

    setButtonRect(rect);
    setPosition({ top, left });
  }, []);

  const schedulePositionUpdate = useCallback(() => {
    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = window.requestAnimationFrame(updatePosition);
  }, [updatePosition]);

  const handleTriggerClick = useCallback(
    (event) => {
      event.stopPropagation();

      if (isOpen) {
        closeMenu();
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      setButtonRect(rect);
      setPosition(null);
      onActiveIdChange?.(id);
    },
    [closeMenu, id, isOpen, onActiveIdChange],
  );

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    schedulePositionUpdate();

    return () => {
      window.cancelAnimationFrame(frameRef.current);
    };
  }, [isOpen, schedulePositionUpdate, children]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        panelRef.current?.contains(event.target) ||
        triggerRef.current?.contains(event.target)
      ) {
        return;
      }

      closeMenu();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [closeMenu, isOpen]);

  return (
    <div className={["new-floating-action-menu", className].filter(Boolean).join(" ")}>
      <button
        ref={triggerRef}
        type="button"
        className={[
          "new-floating-action-menu__trigger",
          triggerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={handleTriggerClick}
      >
        {renderTrigger ? renderTrigger({ isOpen }) : null}
      </button>

      {isOpen && buttonRect
        ? createPortal(
            <div
              ref={panelRef}
              className={[
                "new-floating-action-menu__panel",
                panelClassName,
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                position: "fixed",
                right: "auto",
                bottom: "auto",
                transform: "none",
                top: position?.top ?? buttonRect.bottom + MENU_GAP,
                left: position?.left ?? buttonRect.left,
                visibility: position ? "visible" : "hidden",
                zIndex: 9999,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
